import { useRouter } from 'next/router';
import { FC, Fragment } from 'react';
import styled, { css, keyframes } from 'styled-components';

import Container from '@/components/Container/Container';
import LocalizedLink from '@/components/LocalizedLink/LocalizedLink';
import { LANGUAGES } from '@/lib/constants';
import { getImage } from '@/lib/fetchAPI';

const Hamburger: FC<any> = ({ data, lang, openedHamburger }) => {
	const router = useRouter();
	const link = router?.asPath?.split('/')?.slice(2)?.join('/');

	return (
		<HamburgerMenu
			className={`hamburger-menu-mobile ${openedHamburger ? `visible` : ''}`}
		>
			<HamburgerMenuContent>
				<Container>
					<HamburgerLanguages>
						{LANGUAGES.map(({ code }) => (
							<HamburgerLanguage
								key={`hamburger_language_${code}`}
								$langId={code}
								$activeLang={lang}
							>
								<a href={`/${code}/${link}`}>{code}</a>
							</HamburgerLanguage>
						))}
					</HamburgerLanguages>

					{data?.map((item, index: number) => (
						<Fragment key={`hamburger_navigation_${index}`}>
							{item?.dropdownItem?.length === 0 ? (
								<HamburgerGroupElement className={item?.className}>
									<LocalizedLink href={item?.link}>{item?.name}</LocalizedLink>
								</HamburgerGroupElement>
							) : (
								<>
									<HamburgerGroupTitle className={item?.className}>
										{item?.name}
									</HamburgerGroupTitle>

									{item?.dropdownItem?.map((subItem, subIndex) => (
										<HamburgerGroupElement
											key={`hamburger_subitem_${subIndex}`}
											className={subItem.class}
										>
											{subItem.image?.[0] ? (
												<>
													<a
														target="_blank"
														rel="noopener noreferrer"
														href={subItem.link}
													>
														{subItem.name}
													</a>

													<img
														alt={subItem.image?.name}
														src={getImage(subItem.image)}
													/>
												</>
											) : (
												<LocalizedLink href={subItem?.link}>
													{subItem?.name}
												</LocalizedLink>
											)}
										</HamburgerGroupElement>
									))}
								</>
							)}
						</Fragment>
					))}
				</Container>
			</HamburgerMenuContent>
		</HamburgerMenu>
	);
};

const fadeInUp = keyframes`
    from {
      transform: translateY(20px);
      opacity: 0;
    }

    to {
      transform: translateY(0px);
      opacity: 1;
    }
`;

const HamburgerGroupTitle = styled.div`
	font-family: var(--font-sans);

	font-weight: var(--font-medium);
	font-size: 12px;
	line-height: 15px;
	color: var(--color-purple-50);
	margin-top: 35px;
	margin-bottom: 9px;

	@media (max-height: 650px) {
		margin-top: 20px;
		margin-bottom: 7px;
	}
`;

const HamburgerGroupElement = styled.div`
	margin-bottom: 13px;
	display: flex;

	@media (max-height: 700px) {
		margin-bottom: 8px;
	}

	a {
		display: block;
		font-family: var(--font-sans);

		font-size: 26px;
		line-height: 31px;
		text-decoration: none;
		color: var(--color-purple);

		@media (max-height: 730px) {
			font-size: 21px;
			line-height: 28px;
		}

		@media (max-height: 600px) {
			font-size: 18px;
			line-height: 26px;
		}
	}
`;

const HamburgerMenu = styled.div`
	position: fixed;
	overflow: hidden;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	z-index: 1900;
	transition: 0.5s;
	transform: translateY(-100%);
	background-color: var(--color-white-30);
	display: flex;
	flex-direction: column;
	padding-top: 75px;
	padding-bottom: 25px;
	padding-left: 15px;

	@media (max-height: 610px) {
		padding-top: 66.5px;
	}

	@media (max-height: 560px) {
		padding-top: 76.5px;
	}

	@media (max-width: 900px) {
		&.visible {
			transform: translateY(0);
			visibility: visible;

			${HamburgerGroupElement}, ${HamburgerGroupTitle} {
				transform: translateY(20px);
				opacity: 0;
				animation: ${fadeInUp} 0.3s 1 forwards;
				animation-delay: 0.3s;

				&.group-el-1 {
					animation-delay: 0.3s;
				}

				&.group-el-2 {
					animation-delay: 0.3s;
				}

				&.group-el-3 {
					animation-delay: 0.4s;
				}

				&.group-el-4 {
					animation-delay: 0.5s;
				}

				&.group-el-5 {
					animation-delay: 0.6s;
				}

				&.group-el-6 {
					animation-delay: 0.67s;
				}

				&.group-el-7 {
					animation-delay: 0.75s;
				}

				&.group-el-8 {
					animation-delay: 0.8s;
				}

				&.group-el-9 {
					animation-delay: 0.87s;
				}
			}
		}
	}
`;

const HamburgerMenuContent = styled.div`
	padding-top: 5px;
	padding-bottom: 40px;
`;

const HamburgerLanguages = styled.ul`
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 30px;

	@media (max-height: 650px) {
		margin-bottom: 15px;
	}
`;

const HamburgerLanguage = styled.li<{ $langId: string; $activeLang: string }>`
	width: 75px;
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 12px;
	line-height: 40px;
	text-align: center;
	text-transform: uppercase;

	@media (max-height: 600px) {
		line-height: 36px;
		width: 70px;
	}

	a {
		text-decoration: none;
		color: var(--color-purple-50);
	}

	${({ $langId, $activeLang }) =>
		$langId === $activeLang &&
		css`
			background-color: var(--color-white);
			border-radius: 12px;
			a {
				color: var(--color-black);
			}
		`}
`;

export default Hamburger;
